// Fan-out aggregation view (§6.9, Phase 7). For a targetType='group' task, each scheduled slot
// fans out into one run per group member. This page DERIVES the per-slot rollup (admin
// /api/v1/fanout/:taskId) and lets you drill into the member runs for any slot.

import { Fragment, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Api, type FanoutSlot, type FanoutSlotStatus } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtTime } from "../lib/format";
import { Badge, Card, Empty, ErrorBox, Spinner, StatusBadge } from "../components/ui";

function slotTone(status: FanoutSlotStatus): "ok" | "warn" | "danger" | "muted" {
  switch (status) {
    case "succeeded":
      return "ok";
    case "partial":
      return "warn";
    case "failed":
      return "danger";
    case "running":
    case "skipped":
    default:
      return "muted";
  }
}

function MemberRuns({ taskId, slot }: { taskId: string; slot: number }) {
  const members = useAsync(() => Api.fanoutMembers(taskId, slot), [taskId, slot]);
  if (members.loading && !members.data) return <Spinner />;
  if (members.error) return <ErrorBox error={members.error} />;
  if (!members.data || members.data.members.length === 0) {
    return <Empty>No member runs recorded for this slot.</Empty>;
  }
  return (
    <table className="w-full">
      <thead>
        <tr>
          <th className="th">Member</th>
          <th className="th">Run</th>
          <th className="th">Status</th>
          <th className="th">HTTP</th>
          <th className="th">Attempt</th>
          <th className="th">Finished</th>
          <th className="th">Error</th>
        </tr>
      </thead>
      <tbody>
        {members.data.members.map((m) => (
          <tr key={m.id}>
            <td className="td mono text-xs">#{m.groupMemberIndex ?? "—"}</td>
            <td className="td">
              <Link className="text-primary hover:underline mono text-xs" to={`/runs/${m.id}`}>
                {m.id}
              </Link>
            </td>
            <td className="td">
              <StatusBadge status={m.status} />
            </td>
            <td className="td mono text-xs">{m.responseStatus ?? "—"}</td>
            <td className="td mono text-xs">{m.attempt}</td>
            <td className="td mono text-xs">{fmtTime(m.finishedAt)}</td>
            <td className="td mono text-xs text-danger">{m.error ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function FanoutPage() {
  const { id = "" } = useParams();
  const slots = useAsync(() => Api.fanoutSlots(id), [id]);
  const [openSlot, setOpenSlot] = useState<number | null>(null);

  if (slots.loading && !slots.data) return <Spinner />;
  if (slots.error) return <ErrorBox error={slots.error} />;
  if (!slots.data) return null;

  const rows: FanoutSlot[] = slots.data.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Fan-out</h1>
          <div className="text-sm text-muted">
            <Link className="text-primary hover:underline" to={`/tasks/${id}`}>
              {id}
            </Link>{" "}
            · per-slot member rollup
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn" onClick={() => slots.reload()}>
            Refresh
          </button>
          <Link className="btn" to={`/tasks/${id}`}>
            Back
          </Link>
        </div>
      </div>

      <Card title="Slots">
        {rows.length === 0 ? (
          <Empty>No fan-out runs recorded yet.</Empty>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">Slot</th>
                <th className="th">Status</th>
                <th className="th">Members</th>
                <th className="th">Succeeded</th>
                <th className="th">Failed</th>
                <th className="th">Dead</th>
                <th className="th">Skipped</th>
                <th className="th">In flight</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <Fragment key={s.slot}>
                  <tr>
                    <td className="td mono text-xs">{fmtTime(s.slot)}</td>
                    <td className="td">
                      <Badge tone={slotTone(s.status)}>{s.status}</Badge>
                    </td>
                    <td className="td mono text-xs">{s.members}</td>
                    <td className="td mono text-xs text-ok">{s.succeeded}</td>
                    <td className="td mono text-xs text-danger">{s.failed}</td>
                    <td className="td mono text-xs text-danger">{s.dead}</td>
                    <td className="td mono text-xs text-muted">{s.skipped}</td>
                    <td className="td mono text-xs">{s.pending}</td>
                    <td className="td">
                      <button
                        className="btn"
                        onClick={() => setOpenSlot(openSlot === s.slot ? null : s.slot)}
                      >
                        {openSlot === s.slot ? "Hide" : "Members"}
                      </button>
                    </td>
                  </tr>
                  {openSlot === s.slot && (
                    <tr>
                      <td className="td" colSpan={9}>
                        <MemberRuns taskId={id} slot={s.slot} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}